import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { MenuItem, Paper, Select } from '@mui/material';

// Services
import { getExports, downloadExport, deleteExport, sendExportEmail } from '../../../services/export.service';

// Hooks
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import useConfirmDialog from '../../../hooks/useConfirmDialog';

// Utils
import { Storage } from '../../../utils/Storage';
import { makeExportListColumn } from '../../../utils/columns/makeExportListColumn';

// Types
import { ExportJob, ExportStatus } from '../../../types/Exports';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { SearchBarFilterTypes, CONFIRM_DIALOG_EXPORT_DELETE } from '../../../constants/Constants';

// Common Components
import { Table, useNewTable } from '../../common/table/index';
import SearchBar from '../../common/SearchBar/SearchBar';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

// Components
import { ExportBusyContext, ExportActionKind } from './ExportRowActions';

const POLL_INTERVAL = 5000; // ms

type StatusFilter = ExportStatus | undefined;

const DEFAULT_SORT = [{ createdDateTime: '-1' }];

const isPending = (job: ExportJob): boolean => job.isProcessing || job.status === ExportStatus.Pending || job.status === ExportStatus.Processing;

// Builds the saved file name as "export-<entity>-<date>.csv",
// e.g. "export-device-2026-7-3 16_4_55.csv".
const buildDownloadName = (job: ExportJob): string => {
  const date = moment(job.createdDateTime).format('YYYY-M-D H_m_s');
  const entity = job.entityType.toLowerCase();
  return `export-${entity}-${date}.csv`;
};

export default function Downloads(): JSX.Element {
  const { notifyMessage } = useNotifyMessage();
  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_EXPORT_DELETE);

  // Keep notify identity stable for async handlers so a toast still fires
  // even if the row/page has unmounted (e.g. after navigating away).
  const notifyRef = useRef(notifyMessage);
  useEffect(() => {
    notifyRef.current = notifyMessage;
  }, [notifyMessage]);

  const [status, setStatus] = useState<StatusFilter>(undefined);
  // No entityType here on purpose: this page lists exports across all entities.
  const [paramFilter, setParamFilter] = useState<any>({ filters: [], sortBy: DEFAULT_SORT });

  // Busy state lives here (keyed by job id), NOT inside each row. This ties the
  // loader to the data rather than the row position, so a delete/refetch can no
  // longer leave a spinner stuck on whichever row slides into that slot.
  const [busy, setBusy] = useState<Record<string, ExportActionKind>>({});
  const markBusy = useCallback((id: string, kind: ExportActionKind) => setBusy((b) => ({ ...b, [id]: kind })), []);
  const clearBusy = useCallback(
    (id: string) =>
      setBusy((b) => {
        const next = { ...b };
        delete next[id];
        return next;
      }),
    [],
  );

  // Force useNewTable to refetch (same trick Devices uses after a mutation).
  const refetch = useCallback(() => setParamFilter((prev: any) => ({ ...prev, sortBy: DEFAULT_SORT, changedAt: new Date() })), []);

  const handleDownload = useCallback(
    async (job: ExportJob) => {
      markBusy(job.id, 'download');
      try {
        await downloadExport(job.id, buildDownloadName(job));
        notifyRef.current('Download completed.', 'success');
      } catch (error) {
        notifyRef.current('Download failed. Please try again.', 'error');
      } finally {
        clearBusy(job.id);
      }
    },
    [markBusy, clearBusy],
  );

  const handleEmail = useCallback(
    async (job: ExportJob) => {
      markBusy(job.id, 'email');
      try {
        await sendExportEmail(job.id);
        notifyRef.current('Export emailed to your inbox.', 'success');
      } catch (error) {
        notifyRef.current('Could not send the email.', 'error');
      } finally {
        clearBusy(job.id);
      }
    },
    [markBusy, clearBusy],
  );

  const handleDelete = useCallback(
    async (job: ExportJob) => {
      const { accepted } = await confirm({});
      if (!accepted) return;

      markBusy(job.id, 'delete');
      try {
        await deleteExport(job.id);
        notifyRef.current('Export deleted.', 'success');
        refetch();
      } catch (error) {
        notifyRef.current('Could not delete the export.', 'error');
      } finally {
        clearBusy(job.id);
      }
    },
    [confirm, markBusy, clearBusy, refetch],
  );

  // Stable columns: they only depend on the (stable) handlers, so busy changes
  // do not rebuild columns or trigger extra fetches.
  const columns = useMemo(
    () => makeExportListColumn({ onDownload: handleDownload, onEmail: handleEmail, onDelete: handleDelete }),
    [handleDownload, handleEmail, handleDelete],
  );

  const table = useNewTable<ExportJob>({
    columns,
    fetchAsync: getExports,
    params: paramFilter,
    serverSide: true,
  });

  // Auto-refresh while any job is still Pending / Processing.
  const rows: ExportJob[] = (table as unknown as { data?: ExportJob[] }).data ?? [];
  useEffect(() => {
    if (!rows.some(isPending)) return;
    const timer = setTimeout(() => refetch(), POLL_INTERVAL);
    return () => clearTimeout(timer);
  }, [rows, refetch]);

  // The table's loading overlay dims the grid and blocks scrolling/clicks.
  // We only want it on the very first load, never on the 5s background polls.
  const tableLoading = (table as unknown as { loading?: boolean }).loading ?? false;
  const [initialLoad, setInitialLoad] = useState(true);
  useEffect(() => {
    if (rows.length > 0) setInitialLoad(false);
  }, [rows.length]);

  const onSearchChange = (filters: any) => {
    const finalFilters = [...filters];

    if (status !== undefined) {
      // Serialises to filters[i].fieldName=Status&filters[i].fieldValue=<enum>
      finalFilters.push({ Status: status, filterType: FilterableTypes.Equals });
    }

    setParamFilter({ filters: finalFilters, sortBy: DEFAULT_SORT });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.EXPORTS, { isMoreFilter: true, filters: finalFilters });
  };

  return (
    <React.Fragment>
      {renderDialog()}
      <SearchBar
        pageKey={SearchBarFilterTypes.EXPORTS}
        onSearch={onSearchChange}
        onReset={() => {
          setStatus(undefined);
          setParamFilter({ filters: [], sortBy: DEFAULT_SORT });
          Storage.setSearchBarFiltering(SearchBarFilterTypes.EXPORTS, { filters: [] });
        }}
        allowSearchAll={true}
        columns={columns}
        components={
          <div style={{ display: 'flex', marginBottom: 5 }}>
            <FormInputCollection label='Status' style={{ marginRight: '1.5em' }}>
              <Select
                variant='outlined'
                id='status'
                name='status'
                value={status ?? 'all'}
                size='small'
                style={{ width: '150px' }}
                onChange={(e: any) => {
                  setStatus(e.target.value === 'all' ? undefined : (Number(e.target.value) as ExportStatus));
                }}
              >
                <MenuItem value='all'>All</MenuItem>
                <MenuItem value={ExportStatus.Completed}>
                  <StatusDot color='#2e7d32' /> Ready
                </MenuItem>
                <MenuItem value={ExportStatus.Processing}>
                  <StatusDot color='#0288d1' /> Processing
                </MenuItem>
                <MenuItem value={ExportStatus.Pending}>
                  <StatusDot color='#e65100' /> Pending
                </MenuItem>
                <MenuItem value={ExportStatus.Failed}>
                  <StatusDot color='#d32f2f' /> Failed
                </MenuItem>
              </Select>
            </FormInputCollection>
          </div>
        }
      />
      <Paper>
        <ExportBusyContext.Provider value={busy}>
          <Table
            {...table}
            loading={initialLoad ? tableLoading : false}
            showSearchBar={false}
            showColumnToggler={false}
            showAction={true}
            canExportData={() => false}
            getCoreRowModel={getCoreRowModel()}
          />
        </ExportBusyContext.Provider>
      </Paper>
    </React.Fragment>
  );
}

const StatusDot = ({ color }: { color: string }): JSX.Element => (
  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, display: 'inline-block', marginRight: 8 }} />
);
